import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Trash2, Plus } from "lucide-react";

/**
 * QuickScopeInput — variantă rapidă a ScopeBuilder.
 *
 * User specifică:
 *  - `ekas_zones`: câte zone EKAS sunt (număr manual, nu se calculează)
 *  - `materials[]`: listă (m², material) — poate adăuga mai multe rânduri
 *
 * Prop:
 *  - value: { ekas_zones: number, materials: [{qty, material, label, unit}] }
 *  - onChange: (newValue) => void
 */
export default function QuickScopeInput({ value, onChange }) {
  const v = value || { ekas_zones: 0, materials: [] };
  const [catalog, setCatalog] = useState({ standard_materials: [], custom_materials: [] });

  useEffect(() => {
    api.get("/scope-catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const allMaterials = [
    ...(catalog.standard_materials || []),
    ...(catalog.custom_materials || []),
  ];

  const setEkasZones = (n) => onChange({ ...v, ekas_zones: parseInt(n || 0) || 0, materials: v.materials || [] });

  const addRow = () => {
    const nextMaterials = [...(v.materials || []), { qty: "", material: "", label: "", unit: "m²", location: "" }];
    onChange({ ...v, materials: nextMaterials });
  };

  const updateRow = (i, patch) => {
    const nextMaterials = (v.materials || []).map((r, idx) => idx === i ? { ...r, ...patch } : r);
    onChange({ ...v, materials: nextMaterials });
  };

  const removeRow = (i) => {
    const nextMaterials = (v.materials || []).filter((_, idx) => idx !== i);
    onChange({ ...v, materials: nextMaterials });
  };

  return (
    <div className="space-y-3" data-testid="quick-scope-input">
      <div>
        <Label className="small-label">🟦 Câte zone EKAS?</Label>
        <Input
          type="number"
          min={0}
          step={1}
          value={v.ekas_zones || 0}
          onChange={(e) => setEkasZones(e.target.value)}
          className="mt-1 max-w-[140px]"
          placeholder="0"
          data-testid="quick-scope-ekas-zones"
        />
      </div>
      <div>
        <Label className="small-label">📦 Materiale de scos (m² + tip + unde)</Label>
        <div className="space-y-2 mt-1">
          {(v.materials || []).map((row, i) => (
            <div key={i} className="grid grid-cols-[90px_1fr_150px_40px_32px] gap-2 items-center" data-testid={`quick-scope-material-row-${i}`}>
              <Input
                type="number"
                min={0}
                step="any"
                value={row.qty || ""}
                onChange={(e) => updateRow(i, { qty: parseFloat(e.target.value) || 0 })}
                placeholder="150"
                data-testid={`quick-scope-material-qty-${i}`}
              />
              <Select value={row.material || ""} onValueChange={(val) => {
                const m = allMaterials.find((x) => x.key === val);
                updateRow(i, { material: val, label: m?.label || val, unit: m?.unit || "m²" });
              }}>
                <SelectTrigger data-testid={`quick-scope-material-select-${i}`}>
                  <SelectValue placeholder="Alege material..."/>
                </SelectTrigger>
                <SelectContent>
                  {allMaterials.map((m) => (
                    <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={row.location || ""}
                onChange={(e) => updateRow(i, { location: e.target.value })}
                placeholder="Unde? ex: Bad OG"
                title="Locația de unde se scoate materialul (ex: Bad OG, Bad EG, Küche...)"
                data-testid={`quick-scope-material-location-${i}`}
              />
              <div className="text-xs text-zinc-600 font-mono text-center">{row.unit || "m²"}</div>
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="p-1.5 rounded hover:bg-red-100 text-red-600 flex items-center justify-center"
                data-testid={`quick-scope-material-remove-${i}`}
                title="Șterge rând"
              >
                <Trash2 size={14}/>
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addRow}
            className="w-full text-left rounded-md border-2 border-dashed border-zinc-300 bg-zinc-50 hover:bg-zinc-100 p-2 transition-colors text-zinc-700 font-semibold text-sm flex items-center gap-2 justify-center"
            data-testid="quick-scope-add-material"
          >
            <Plus size={14}/> Adaugă material
          </button>
        </div>
      </div>
    </div>
  );
}
